import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import translations from './../data/translations';
import ContactPopup from "../components/ContactPopup";

export default function Scoreboard() {
  const [scores, setScores] = useState([]);
  const currentUser = localStorage.getItem('nickname') || 'Anonymous';
  const apiUrl = window.APP_CONFIG?.apiBaseUrl;
  const leadsIngestUrl = window.APP_CONFIG?.leadsIngestUrl;

const [language, setLanguage] = useState(localStorage.getItem('language') || 'nl');
 const t = translations[language] || translations.en;

  useEffect(() => {
    if (!apiUrl) return; // avoid fetching "undefined/api/scores" on first paint

    const controller = new AbortController();

    fetch(`${apiUrl}/api/scores`, {
      headers: { 'X-Game-Token': 'b73ebff9-4537-477b-97f5-ebb968bfbe40' },
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        const dur = (x) =>
          typeof x.duration_ms === 'number' ? x.duration_ms : Number.POSITIVE_INFINITY;

        // score desc, then time asc, then earlier date
        const sorted = [...data].sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          const da = dur(a), db = dur(b);
          if (da !== db) return da - db;
          return new Date(a.date) - new Date(b.date);
        });

        setScores(sorted);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch scores', err);
        }
      });

    return () => controller.abort();
  }, [apiUrl]);

  const handleLanguageChange = (e) => {
    const selected = e.target.value;
    localStorage.setItem('language', selected);
    setLanguage(selected);
  };

const localeMap = { nl: 'nl-BE', en: 'en-US', fr: 'fr-BE' };
 const fmtDate = (d) => new Date(d).toLocaleString(localeMap[language] || 'en-US');

  const fmtTime = (ms) => {
    if (typeof ms !== 'number') return '–';
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // Calculate which entries to display (top 10 + current user if ranked lower)
  const getDisplayEntries = () => {
    if (scores.length === 0) return [];
    
    const top10 = scores.slice(0, 10);
    
    // If no current user or user is "Anonymous", just show top 10
    if (!currentUser || currentUser === 'Anonymous') {
      return top10.map((entry, index) => ({ ...entry, displayRank: index + 1 }));
    }
    
    const currentUserIndex = scores.findIndex(entry => entry.nickname === currentUser);
    
    // If current user is in top 10 or doesn't exist in scoreboard, just show top 10
    if (currentUserIndex === -1 || currentUserIndex < 10) {
      return top10.map((entry, index) => ({ ...entry, displayRank: index + 1 }));
    }
    
    // If current user is ranked 11th or lower, show top 10 + current user
    const currentUserEntry = scores[currentUserIndex];
    const entriesWithRanks = [
      ...top10.map((entry, index) => ({ ...entry, displayRank: index + 1 })),
      { ...currentUserEntry, displayRank: currentUserIndex + 1, isCurrentUserLowerRank: true }
    ];
    
    return entriesWithRanks;
  };

  const displayEntries = getDisplayEntries();

  return (
    <div className="min-h-screen bg-[#0d1b2a] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Gradient Blobs */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute w-72 h-72 bg-[#00aca9] opacity-20 rounded-full blur-3xl top-10 left-[-100px]" />
        <div className="absolute w-80 h-80 bg-[#f68d43] opacity-20 rounded-full blur-3xl bottom-10 right-[-80px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white shadow-2xl rounded-2xl p-8 w-full max-w-3xl z-10"
      >
        <div className="flex justify-end mb-2">
          <select
            value={language}
            onChange={handleLanguageChange}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
          >
            <option value="nl">NL</option>
            <option value="en">EN</option>
            <option value="fr">FR</option>
          </select>
        </div>

        <h1 className="text-2xl font-bold text-[#2b3b4b] mb-4 text-center">{t.scoreboardTitle}</h1>
        <p className="text-sm text-gray-600 mb-6 text-center">{t.scoreboardSubtitle}</p>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-[#f4f4f4] text-[#2b3b4b] font-semibold">
                <th className="py-3 px-4 text-left">{t.rank}</th>
                <th className="py-3 px-4 text-left">{t.name}</th>
                <th className="py-3 px-4 text-left">{t.score}</th>
<th className="py-3 px-4 text-left">{t.time}</th>
                <th className="py-3 px-4 text-left">{t.date}</th>
              </tr>
            </thead>
            <tbody>
              {displayEntries.map((entry, index) => {
                const isCurrentUser = entry.nickname === currentUser;
                const isLowerRankUser = entry.isCurrentUserLowerRank;
                
                return (
                  <React.Fragment key={`${entry.nickname}-${entry.date}-${index}`}>
                    {/* Add separator row before lower ranked current user */}
                    {isLowerRankUser && (
                      <tr>
                        <td colSpan="5" className="py-1 px-4">
                          <div className="flex items-center">
                            <div className="flex-1 border-t border-gray-300"></div>
                            <span className="px-3 text-xs text-gray-500 bg-white">{t.yourRanking}</span>
                            <div className="flex-1 border-t border-gray-300"></div>
                          </div>
                        </td>
                      </tr>
                    )}
                    
                    <tr
                      className={`transition ${
                        isCurrentUser
                          ? 'bg-[#00aca9]/20 font-bold text-[#2b3b4b]'
                          : index % 2 === 0
                          ? 'bg-[#f6fafc]'
                          : 'bg-white'
                      }`}
                    >
                      <td className="py-2 px-4 font-mono">{entry.displayRank}</td>
                      <td className="py-2 px-4">{entry.nickname}</td>
                      <td className="py-2 px-4">{entry.score}</td>
                      <td className="py-2 px-4">{fmtTime(entry.duration_ms)}</td>
                      <td className="py-2 px-4 text-xs text-gray-500">{fmtDate(entry.date)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>
       <ContactPopup apiUrl={apiUrl} leadsIngestUrl={leadsIngestUrl} delayMs={3000} />
    </div>
  );
  
}
